// =====================================================================
// CreditPreflightDialog
// =====================================================================
// Friendly modal shown by `useCreditPreflight` when an AI job is likely
// to exceed the user's available credits. Three actions:
//   - Buy credits  (deep-links to Settings buy-credits dialog)
//   - Continue anyway  (caches "skip" for this workspace+job for the session)
//   - Cancel  (default)
// =====================================================================

import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { labelForJob, type PreflightDecision } from "@/lib/aiCreditPreflight";
import type { AiJobKind } from "@/lib/aiCreditEstimates";

type Props = {
  open: boolean;
  job: AiJobKind | null;
  decision: Extract<PreflightDecision, { ok: false }> | null;
  onCancel: () => void;
  onContinue: () => void;
};

const fmt = (n: number) => (n < 0 ? "—" : n.toLocaleString());

export const CreditPreflightDialog = ({
  open,
  job,
  decision,
  onCancel,
  onContinue,
}: Props) => {
  const navigate = useNavigate();
  if (!decision || !job) return null;

  const { stats, estimate, remainingMonthly, totalAvailable } = decision;
  const shortfall = estimate - totalAvailable;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            This will use about {estimate} credits
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-medium text-foreground">{labelForJob(job)}</span> needs more
            credits than you currently have. The job will likely fail mid-way.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <dl className="my-2 space-y-1.5 rounded-md border border-border bg-muted/40 p-3 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-muted-foreground">Monthly remaining</dt>
            <dd className="font-medium">{fmt(remainingMonthly)}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-muted-foreground">Top-up balance</dt>
            <dd className="font-medium">{fmt(stats.topup_credits_balance)}</dd>
          </div>
          <div className="flex items-baseline justify-between border-t border-border/60 pt-1.5">
            <dt className="text-muted-foreground">Total available</dt>
            <dd className="font-semibold">{fmt(totalAvailable)}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-muted-foreground">Estimated cost</dt>
            <dd className="font-medium">{estimate.toLocaleString()}</dd>
          </div>
          <div className="flex items-baseline justify-between border-t border-border/60 pt-1.5">
            <dt className="text-destructive">Short by</dt>
            <dd className="font-semibold text-destructive">{shortfall.toLocaleString()}</dd>
          </div>
        </dl>

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onContinue}>
            Continue anyway
          </Button>
          <Button
            onClick={() => {
              onCancel();
              navigate("/settings/credits?buy_credits=1");
            }}
          >
            Buy credits
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
